import { UseQueryResult } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiResponse } from "@/api/rest/core/types";
import { UserInfo } from "@/api/rest/models/account";
import { Building } from "@/api/rest/models/building";
import { Household } from "@/api/rest/models/household";
import { firstHouseholdId, sortBuildingsByHouseholdSequence } from "@/utils/collection";

import { NavbarLogo } from "./NavbarLogo";
import { NavbarMenu } from "./NavbarMenu";

// Memoize child components to prevent unnecessary re-renders
const MemoizedNavbarLogo = memo(NavbarLogo);
const MemoizedNavbarMenu = memo(NavbarMenu);

interface NavbarProps {
  households: (Building & { households: Household[] })[];
  currentHousehold: UseQueryResult<ApiResponse<any>, unknown>;
  user: UserInfo | null;
  buildingId?: string;
}

interface Development {
  id: number;
  name: string;
  buildings: (Building & { households: Household[] })[];
}

const Navbar = ({ households, currentHousehold, user, buildingId }: NavbarProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDevelopmentId, setSelectedDevelopmentId] = useState<number | undefined>();

  useEffect(() => {
    const developmentId = currentHousehold.data?.result?.building?.developmentId;
    if (developmentId) {
      setSelectedDevelopmentId(developmentId);
    }
  }, [currentHousehold.data?.result?.building?.developmentId]);

  useEffect(() => {
    if (buildingId) {
      const building = households.find((b) => b.buildingId === Number(buildingId));
      if (building?.developmentId) {
        setSelectedDevelopmentId(building.developmentId);
      }
    }
  }, [buildingId, households]);

  const { developments, selectedDevelopment } = useMemo(() => {
    const buildingsByDev = households.reduce(
      (acc, building) => {
        const devId = building.developmentId ?? -1;
        const devName = building.developmentName ?? "Unknown";
        if (!acc[devId]) {
          acc[devId] = { id: devId, name: devName, buildings: [] };
        }
        acc[devId].buildings.push(building);
        return acc;
      },
      {} as Record<number, Development>,
    );
    const developments = Object.values(buildingsByDev);

    developments.sort((a, b) => {
      const minIdA = Math.min(...a.buildings.map((b) => b.buildingId));
      const minIdB = Math.min(...b.buildings.map((b) => b.buildingId));
      return minIdA - minIdB;
    });

    let selectedDevelopment: Development | undefined;
    if (selectedDevelopmentId !== undefined) {
      selectedDevelopment = developments.find((d) => d.id === selectedDevelopmentId);
    }

    if (!selectedDevelopment && developments.length > 0) {
      selectedDevelopment = developments[0];
    }

    return { developments, selectedDevelopment };
  }, [households, selectedDevelopmentId]);

  const handleToggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);
  const householdData = currentHousehold.data?.result;

  const handleNavigate = useCallback(() => {
    setIsOpen(false);
  }, []);

  const logoProps = useMemo(() => {
    return {
      householdData,
      onToggleMenu: handleToggleMenu,
      developments,
      selectedDevelopment,
      setSelectedDevelopment: (development: Development) => {
        setSelectedDevelopmentId(development.id);
        // Open a flat of the picked development; otherwise the page keeps showing
        // the previous development's flat and its documents under the new sidebar.
        const householdId = firstHouseholdId(development.buildings);
        if (development.id !== selectedDevelopment?.id && householdId !== undefined) {
          navigate(`/household/${householdId}`);
        }
      },
      isMenuOpen: isOpen,
    };
  }, [householdData, handleToggleMenu, developments, selectedDevelopment, setSelectedDevelopmentId, isOpen, navigate]);

  const menuProps = useMemo(() => {
    const buildings = sortBuildingsByHouseholdSequence(
      developments.length > 1 && selectedDevelopment ? selectedDevelopment.buildings : households,
    );
    return {
      isOpen,
      userName: user?.name ?? "",
      user,
      buildings,
      onNavigate: handleNavigate,
    };
  }, [isOpen, user, developments, selectedDevelopment, handleNavigate, households]);

  return (
    <>
      <nav className="min-w-[250px] flex flex-col justify-stretch bg-white">
        <MemoizedNavbarLogo {...logoProps} />
        <MemoizedNavbarMenu {...menuProps} />
      </nav>
    </>
  );
};

// Memoize the entire Navbar component
export default memo(Navbar);
